import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format, addDays } from 'date-fns';
import {
  Flower2,
  Clock,
  Calendar,
  Loader2,
  ChevronRight,
  Star,
  Check,
  MapPin,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function GuestSpaPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading, guest } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [spaServices, setSpaServices] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDate, setBookingDate] = useState(addDays(new Date(), 1));
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);

  useEffect(() => {
    loadProperties();
    if (isAuthenticated) {
      loadMyAppointments();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedProperty) {
      loadSpaServices(selectedProperty);
    }
  }, [selectedProperty]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await guestPortalApi.listProperties();
      setProperties(response.data || []);
      if (response.data?.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSpaServices = async (propertyId) => {
    try {
      const response = await guestPortalApi.listSpaServices(propertyId);
      setSpaServices(response.data || []);
    } catch (error) {
      console.error('Failed to load spa services:', error);
    }
  };

  const loadMyAppointments = async () => {
    try {
      const response = await guestPortalApi.listMySpaAppointments(token);
      setMyAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to load appointments:', error);
    }
  };

  const loadAvailableSlots = async (serviceId, date) => {
    try {
      setLoadingSlots(true);
      const response = await guestPortalApi.checkSpaAvailability(
        selectedProperty,
        serviceId,
        format(date, 'yyyy-MM-dd')
      );
      // Extract just the time strings from slot objects
      const slots = (response.data?.available_slots || []).map(s => typeof s === 'string' ? s : s.time);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Failed to load slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBookService = (service) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to book spa services');
      navigate('/portal/login');
      return;
    }
    setSelectedService(service);
    setShowBookingDialog(true);
    loadAvailableSlots(service.id, bookingDate);
  };

  const handleDateChange = (date) => {
    setBookingDate(date);
    setSelectedSlot(null);
    if (selectedService) {
      loadAvailableSlots(selectedService.id, date);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      await guestPortalApi.cancelSpaAppointment(token, appointmentId, 'Guest requested cancellation');
      toast.success('Appointment cancelled successfully');
      loadMyAppointments();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    try {
      setBooking(true);
      await guestPortalApi.bookSpa(token, {
        property_id: selectedProperty,
        service_id: selectedService.id,
        appointment_date: format(bookingDate, 'yyyy-MM-dd'),
        start_time: selectedSlot,
        guest_name: guest ? `${guest.first_name || ''} ${guest.last_name || ''}`.trim() : 'Guest',
        guest_email: guest?.email || null,
      });
      toast.success('Spa appointment booked successfully!');
      setShowBookingDialog(false);
      setSelectedService(null);
      setSelectedSlot(null);
      loadMyAppointments();
    } catch (error) {
      console.error('Failed to book spa:', error);
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setBooking(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-10 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-80" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="guest-spa-page">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-pink-50 via-purple-50 to-background dark:from-pink-950/20 dark:via-purple-950/20 dark:to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-pink-100 dark:bg-pink-900/30">
                <Flower2 className="h-8 w-8 text-pink-600 dark:text-pink-400" />
              </div>
              <h1 className="text-4xl font-bold">Spa & Wellness</h1>
            </div>
            <p className="text-lg text-muted-foreground mb-6">
              Rejuvenate your mind and body with our premium spa treatments. 
              From relaxing massages to revitalizing facials, our expert therapists 
              are here to help you unwind and restore.
            </p>
            <div className="flex items-center gap-4">
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-64">
                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* My Appointments Section */}
      {isAuthenticated && myAppointments.length > 0 && (
        <section className="py-8 bg-primary/5" data-testid="my-appointments">
          <div className="container mx-auto px-4">
            <h2 className="text-xl font-semibold mb-4">Your Upcoming Appointments</h2>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {myAppointments.map(apt => (
                <Card key={apt.id} className="min-w-[280px] bg-background">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-pink-100 dark:bg-pink-900/30">
                        <Flower2 className="h-5 w-5 text-pink-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{apt.service_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(apt.appointment_date), 'MMM d, yyyy')} at {apt.start_time}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">
                            {apt.status}
                          </Badge>
                          {!['completed', 'cancelled', 'no_show'].includes(apt.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleCancelAppointment(apt.id)}
                              data-testid={`cancel-appointment-${apt.id}`}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8">Our Spa Services</h2>
          
          {spaServices.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Flower2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No spa services available</p>
                <p className="text-muted-foreground">Please check back later or select another property</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spaServices.map(service => (
                <Card 
                  key={service.id} 
                  className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1"
                  data-testid={`spa-service-${service.id}`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-pink-100 via-purple-50 to-rose-100 dark:from-pink-900/30 dark:via-purple-900/20 dark:to-rose-900/30 flex items-center justify-center relative">
                    <Flower2 className="h-20 w-20 text-pink-400/40" />
                    {service.member_price && service.member_price < service.price && (
                      <Badge className="absolute top-3 right-3 bg-amber-500">
                        <Star className="h-3 w-3 mr-1" /> Member Price
                      </Badge>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{service.name}</CardTitle>
                    {service.description && (
                      <CardDescription className="line-clamp-2">
                        {service.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm mb-4">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {service.duration_minutes} minutes
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-lg">ETB {service.price?.toLocaleString()}</span>
                        {service.member_price && service.member_price < service.price && (
                          <p className="text-xs text-muted-foreground">
                            Member: ETB {service.member_price?.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => handleBookService(service)}
                      data-testid={`book-service-${service.id}`}
                    >
                      Book Now
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Book Spa Treatment</DialogTitle>
            <DialogDescription>
              {selectedService?.name} - {selectedService?.duration_minutes} minutes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Date Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <Calendar className="h-4 w-4 mr-2" />
                    {format(bookingDate, 'EEEE, MMMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={bookingDate}
                    onSelect={(date) => date && handleDateChange(date)}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Slots */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Time</label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : availableSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No available slots for this date. Please select another date.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map(slot => (
                    <Button
                      key={slot}
                      variant={selectedSlot === slot ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedSlot(slot)}
                      className="w-full"
                    >
                      {selectedSlot === slot && <Check className="h-3 w-3 mr-1" />}
                      {slot}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Price Summary */}
            {selectedService && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Service</span>
                  <span>{selectedService.name}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Duration</span>
                  <span>{selectedService.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t">
                  <span>Total</span>
                  <span>ETB {selectedService.price?.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmBooking} disabled={booking || !selectedSlot}>
              {booking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
